/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useToaster } from "contexts/toasterContext";
import {
    PackageCheck,
    CreditCard,
    ArrowRight,
    Loader2,
    ArrowUpCircle,
    CheckCircle2
} from "lucide-react";

// Adjust the relative paths to where your config and services are located
import appConfig from "../../src/services/apiConfig";
import apiService from "../../src/services/apiService";

// Define strict types for the parsed response data
interface PlanType {
    id?: string | number;
    name: string;
    chargePerEmployee?: number | string;
    duration?: string | number;
    modules?: string[];
}

// Trial is removed from the available options per previous constraints
const PLAN_OPTIONS = [
    { id: "basic", name: "Basic", tagline: "Essential tools to get started" },
    { id: "premium", name: "Premium", tagline: "Advanced features for growing teams" },
    { id: "enterprise", name: "Enterprise", tagline: "Maximum performance & control" }
];

// Helper to format raw backend module strings
const formatModuleName = (name: string) => {
    if (!name) return "";
    return name
        .replace(/_/g, " ")
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
};

const PlanSelection = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { addToast } = useToaster();
    const hasToasted = useRef(false);

    // 1. EXTRACT FROM URL PARAMETERS
    const searchParams = new URLSearchParams(location.search);
    const querySchemaName = searchParams.get("schemaName");
    const queryPlan = searchParams.get("plan");
    const queryId = searchParams.get("registrationId") || searchParams.get("id");

    const isFromPaymentCallback = !!querySchemaName;

    // 2. STATE RESOLUTION
    const resolvedSchemaName =
        querySchemaName ||
        location.state?.schemaName ||
        sessionStorage.getItem("schemaName") ||
        "";

    const resolvedPlanName =
        queryPlan ||
        location.state?.initialPlanName ||
        location.state?.plan ||
        sessionStorage.getItem("initialPlanName") ||
        sessionStorage.getItem("plan");

    const resolvedIdentifier =
        queryId ||
        location.state?.registrationId ||
        location.state?.id ||
        sessionStorage.getItem("registrationId") ||
        sessionStorage.getItem("userId") ||
        "";

    // --- RENDER FLAGS ---
    const isNewlyRegistered = !resolvedSchemaName;
    const isTrial = resolvedPlanName?.toLowerCase() === "trial";

    const [activePlanName, setActivePlanName] = useState<string>(isTrial ? "" : (resolvedPlanName || ""));
    const [savedPlan, setSavedPlan] = useState<string>(resolvedPlanName || "");

    const [allPlanDetails, setAllPlanDetails] = useState<Record<string, PlanType>>({});
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isCheckingInvoices, setIsCheckingInvoices] = useState<boolean>(!!resolvedSchemaName);
    const [isUpgrading, setIsUpgrading] = useState<boolean>(false);
    const [hasUpgradedLocally, setHasUpgradedLocally] = useState<boolean>(false);

    const isPlanChanged = activePlanName.toLowerCase() !== savedPlan.toLowerCase();
    const isExistingEnterprise = savedPlan?.toLowerCase() === "enterprise";

    const selectedPlanDetails = activePlanName ? allPlanDetails[activePlanName.toLowerCase()] : null;

    useEffect(() => {
        if (!resolvedSchemaName) {
            setIsCheckingInvoices(false);
            return;
        }

        const checkInvoices = async () => {
            try {
                const regApiUrl = appConfig.getRegistrationApiUrl();
                const cleanBaseUrl = regApiUrl.endsWith('/') ? regApiUrl : `${regApiUrl}/`;

                const response = await axios.get(`${cleanBaseUrl}company/pending-invoices?schemaName=${resolvedSchemaName}`);

                const invoices = response.data?.data || response.data || [];
                const pending = Array.isArray(invoices) ? invoices.filter((inv: any) => inv.status === 'PENDING') : [];

                if (pending.length > 0) {
                    navigate('/pending-invoices', { state: location.state });
                } else {
                    setIsCheckingInvoices(false);
                }
            } catch (error) {
                console.error("Failed to check pending invoices:", error);
                setIsCheckingInvoices(false);
            }
        };

        checkInvoices();
    }, [resolvedSchemaName, navigate, location.state]);

    useEffect(() => {
        if (!isCheckingInvoices && isTrial && !hasToasted.current) {
            addToast("Please select a plan.", "info");
            hasToasted.current = true;
        }
    }, [isTrial, isCheckingInvoices, addToast]);

    useEffect(() => {
        if (isCheckingInvoices) return;

        const fetchAllPlanDetails = async () => {
            try {
                setIsLoading(true);
                const regApiUrl = appConfig.getRegistrationApiUrl();
                const cleanBaseUrl = regApiUrl.endsWith('/') ? regApiUrl : `${regApiUrl}/`;

                const detailsMap: Record<string, PlanType> = {};

                const promises = PLAN_OPTIONS.map(async (plan) => {
                    try {
                        const response = await axios.get(`${cleanBaseUrl}plan-types/${plan.name}`);
                        let payload = response.data;
                        if (payload && payload.data) payload = payload.data;

                        let rawPlansArray: any[] = [];
                        if (Array.isArray(payload)) rawPlansArray = payload;
                        else if (typeof payload === "object" && payload !== null) rawPlansArray = [payload];

                        const mappedPlans: PlanType[] = rawPlansArray.map((p: any) => ({
                            id: p.id,
                            name: p.planTypeName || p.name || plan.name,
                            chargePerEmployee: p.chargePerEmployee !== undefined ? p.chargePerEmployee : p.chargePerEmployee,
                            duration: p.duration ? `${p.duration} Days` : p.duration,
                            modules: p.moduleList || p.modules || []
                        }));

                        const match = mappedPlans.find(p => p.name?.toLowerCase() === plan.name.toLowerCase());
                        if (match) {
                            detailsMap[plan.name.toLowerCase()] = match;
                        } else if (mappedPlans.length > 0) {
                            detailsMap[plan.name.toLowerCase()] = mappedPlans[0];
                        }
                    } catch (err) {
                        console.error(`Failed to fetch details for ${plan.name}`, err);
                    }
                });

                await Promise.all(promises);
                setAllPlanDetails(detailsMap);
            } catch (error) {
                addToast("Failed to load plan details.", "error");
            } finally {
                setIsLoading(false);
            }
        };

        fetchAllPlanDetails();
    }, [addToast, isCheckingInvoices]);

    const handlePlanSelection = (newPlanName: string) => {
        setActivePlanName(newPlanName);
    };

    const handleUpgradePlan = async () => {
        if (!resolvedSchemaName && !resolvedIdentifier) {
            addToast("User identification is missing. Cannot proceed with upgrade.", "error");
            return;
        }

        try {
            setIsUpgrading(true);
            const regApiUrl = appConfig.getRegistrationApiUrl();
            const cleanBaseUrl = regApiUrl.endsWith('/') ? regApiUrl : `${regApiUrl}/`;
            let response;

            if (!resolvedSchemaName && resolvedIdentifier) {
                response = await axios.put(
                    `${cleanBaseUrl}company/${resolvedIdentifier}/plan?planType=${activePlanName.toUpperCase()}`
                );
            } else {
                response = await axios.post(`${cleanBaseUrl}payment/upgrade/create-order`, {
                    schemaName: resolvedSchemaName,
                    planType: activePlanName.toUpperCase()
                });
            }

            const successMessage = response.data?.message || "Plan updated successfully.";
            addToast(successMessage, "success");

            if (isTrial) {
                try {
                    if (resolvedSchemaName) await apiService.post("/api/auth/logout", {});
                } catch (logoutError) {
                    console.error("Logout API failed during trial upgrade flow:", logoutError);
                }
                localStorage.clear();
                sessionStorage.clear();
                navigate("/login");
            } else {
                sessionStorage.setItem("plan", activePlanName);
                if (location.state) {
                    location.state.plan = activePlanName;
                    location.state.initialPlanName = activePlanName;
                }
                window.history.replaceState({}, '', window.location.pathname);
                setSavedPlan(activePlanName);
                setHasUpgradedLocally(true);
            }

        } catch (error: any) {
            console.error("Backend plan upgrade sync failed:", error);
            const serverMessage = error.response?.data?.message;
            if (serverMessage) addToast(serverMessage, "error");
            else addToast("An unexpected error occurred. Please try again.", "error");
        } finally {
            setIsUpgrading(false);
        }
    };

    const handleContinueToLogin = async () => {
        try {
            if (resolvedSchemaName) await apiService.post("/api/auth/logout", {});
        } catch (error) {
            console.error("Logout failed:", error);
        } finally {
            localStorage.clear();
            sessionStorage.clear();
            navigate("/login");
        }
    };

    const handleProceedToPayment = () => {
        if (!resolvedIdentifier) {
            addToast("Identifier ID is missing. Please try again.", "error");
            return;
        }
        const paymentUrl = appConfig.getPaymentRedirectUrl(resolvedIdentifier);
        sessionStorage.removeItem("initialPlanName");
        sessionStorage.removeItem("registrationId");
        sessionStorage.removeItem("plan");
        sessionStorage.removeItem("schemaName");
        window.location.href = paymentUrl;
    };

    if (isCheckingInvoices) {
        return (
            <div className="min-h-[calc(100vh-70px)] flex items-center justify-center p-4 bg-slate-50 dark:bg-gray-900">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                    <p className="text-gray-600 dark:text-gray-400 font-medium">Checking account status...</p>
                </div>
            </div>
        );
    }

    if (!resolvedPlanName) {
        return (
            <div className="min-h-[calc(100vh-70px)] flex items-center justify-center p-4 bg-slate-50 dark:bg-gray-900">
                <p className="text-gray-600 dark:text-gray-400">Invalid or expired session. Redirecting...</p>
                {setTimeout(() => navigate(-1), 2000) && null}
            </div>
        );
    }

    const shouldShowContinueToLogin =
        isFromPaymentCallback ||
        (hasUpgradedLocally && !isNewlyRegistered) ||
        (!isNewlyRegistered && isExistingEnterprise);

    return (
        /* 
           ROOT WRAPPER 
           - Uses 'h-[calc(100vh-70px)]' and 'overflow-hidden' to strictly bind the whole module to the exact viewport height.
           - Flex layout with structured vertical spacing ensures cards share the space safely while leaving room for footer buttons.
        */
        <div className="w-full h-[calc(100vh-70px)] bg-slate-50 dark:bg-gray-900 px-6 py-4 flex flex-col overflow-hidden">

            {/* TOP CONTENT WRAPPER (Header + Cards Grid) */}
            <div className="flex flex-col flex-1 min-h-0 gap-4">

                {/* 1. HEADER */}
                <div className="shrink-0 flex items-center gap-4 pb-4">
                    <div className="w-10 h-10 rounded-xl bg-blue-100/60 dark:bg-blue-900/40 flex items-center justify-center shadow-sm">
                        <PackageCheck className="text-blue-600 dark:text-blue-400 w-5 h-5" strokeWidth={2.5} />
                    </div>
                    <div>
                        <h1 className="text-xl font-bold text-gray-900 dark:text-white leading-tight">
                            Plan Selection
                        </h1>
                        <p className="text-gray-500 dark:text-gray-400 text-xs mt-0.5">
                            Choose the subscription that fits your needs
                        </p>
                    </div>
                </div>

                {/* 2. CARDS GRID 
                    - Reduced padding and optimized spacing so cards fit comfortably without overflowing.
                */}
                <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-6 min-h-0">
                    {isLoading ? (
                        <div className="flex flex-col items-center justify-center col-span-1 md:col-span-3 w-full h-full">
                            <Loader2 className="w-10 h-10 text-blue-600 dark:text-blue-500 animate-spin mb-4" />
                            <p className="text-gray-500 dark:text-gray-400 font-medium">Loading available plans...</p>
                        </div>
                    ) : (
                        PLAN_OPTIONS.map((plan) => {
                            const isSelected = activePlanName.toLowerCase() === plan.name.toLowerCase();
                            const isDisabled = isExistingEnterprise && plan.name.toLowerCase() !== "enterprise";
                            const details = allPlanDetails[plan.name.toLowerCase()];

                            return (
                                <div
                                    key={plan.id}
                                    className={`relative flex flex-col h-full w-full bg-white dark:bg-[#151b23] p-6 lg:p-7 rounded-[1.25rem] transition-all duration-300 min-h-0 ${isSelected
                                        ? 'border-2 border-blue-600 shadow-xl shadow-blue-600/10 dark:border-blue-500 z-10'
                                        : 'border border-gray-200 shadow-sm dark:border-gray-800 hover:border-blue-300 dark:hover:border-blue-600'
                                        } ${isDisabled ? 'opacity-50 grayscale cursor-not-allowed' : ''}`}
                                >
                                    {isDisabled && (
                                        <div className="absolute top-4 right-4">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 dark:bg-gray-800 dark:text-gray-400 rounded px-2 py-1">
                                                Unavailable
                                            </span>
                                        </div>
                                    )}

                                    {/* Card Header */}
                                    <div className="shrink-0 mb-3">
                                        <h3 className={`text-xl font-bold mb-1 ${isSelected ? 'text-blue-700 dark:text-blue-400' : 'text-gray-900 dark:text-white'}`}>
                                            {plan.name}
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 min-h-[32px] pr-2">
                                            {plan.tagline}
                                        </p>
                                    </div>

                                    {/* Pricing Section */}
                                    <div className="shrink-0 mb-4">
                                        <div className="flex items-baseline gap-1 mb-0.5">
                                            <span className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">
                                                {details?.chargePerEmployee !== undefined ? (typeof details.chargePerEmployee === 'number' ? `INR ${details.chargePerEmployee} per employee` : details.chargePerEmployee) : "Custom"}
                                            </span>
                                        </div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                                            {details?.duration ? `Per ${String(details.duration).replace(/days/i, 'Days')}` : 'Custom Pricing'}
                                        </p>
                                    </div>

                                    {/* Selection Button */}
                                    <button
                                        type="button"
                                        onClick={() => !isDisabled && handlePlanSelection(plan.name)}
                                        disabled={isDisabled}
                                        className={`shrink-0 w-full py-2.5 rounded-xl font-bold transition-all mb-4 text-sm border ${isSelected
                                            ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:border-blue-500 dark:hover:bg-blue-600 shadow-md shadow-blue-600/20'
                                            : 'bg-transparent text-blue-600 border-blue-200 hover:border-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:border-blue-800 dark:hover:bg-blue-900/20'
                                            }`}
                                    >
                                        {isSelected ? 'Selected' : `Select ${plan.name}`}
                                    </button>

                                    {/* MODULES LIST AREA */}
                                    <div className="flex flex-col flex-1 min-h-0">
                                        <h4 className="shrink-0 text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-3">
                                            Everything in {plan.name} includes:
                                        </h4>

                                        <div className="flex-1 min-h-0 overflow-y-auto pr-2 pb-1">
                                            <ul className="space-y-2.5">
                                                {details?.modules && details.modules.length > 0 ? (
                                                    details.modules.map((module, idx) => (
                                                        <li key={idx} className="flex items-start gap-2.5 text-xs font-medium text-gray-700 dark:text-gray-300 group">
                                                            <CheckCircle2 className="w-4 h-4 text-green-500 mt-[1px] shrink-0" strokeWidth={2.5} />
                                                            <span className="leading-snug">{typeof module === 'string' ? formatModuleName(module) : JSON.stringify(module)}</span>
                                                        </li>
                                                    ))
                                                ) : (
                                                    <li className="text-xs text-gray-400 italic">No specific features listed.</li>
                                                )}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* 3. ACTION BUTTONS FOOTER 
                - Sits securely right at the bottom edge, fully visible and accessible.
            */}
            <div className="mt-auto shrink-0 flex flex-col md:flex-row items-center justify-between gap-4 pt-5">
                {/* Left side: Upgrade Plan Button */}
                <button
                    type="button"
                    onClick={handleUpgradePlan}
                    disabled={!selectedPlanDetails || isLoading || isUpgrading || !isPlanChanged}
                    className="w-full md:w-auto px-8 flex items-center justify-center gap-2 h-10 rounded-xl border-2 border-blue-600 text-blue-600 bg-white hover:bg-blue-50 dark:bg-gray-800 dark:text-blue-400 dark:border-blue-500 dark:hover:bg-blue-900/20 disabled:opacity-40 disabled:border-gray-300 disabled:text-gray-400 disabled:dark:border-gray-700 disabled:dark:text-gray-600 disabled:cursor-not-allowed font-bold text-sm transition-colors"
                >
                    {isUpgrading ? (
                        <>
                            <Loader2 size={16} className="animate-spin" />
                            Upgrading...
                        </>
                    ) : (
                        <>
                            <ArrowUpCircle size={16} />
                            Upgrade Plan
                        </>
                    )}
                </button>

                {/* Right side: Navigation Buttons */}
                {shouldShowContinueToLogin ? (
                    <button
                        type="button"
                        onClick={handleContinueToLogin}
                        disabled={isPlanChanged || isUpgrading}
                        className="w-full md:w-auto px-8 flex items-center justify-center gap-2 h-10 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm transition-colors shadow-md shadow-blue-600/20"
                    >
                        Continue to Login
                        <ArrowRight size={14} className="ml-1" />
                    </button>
                ) : (
                    <>
                        {!isTrial && isNewlyRegistered && (
                            <button
                                type="button"
                                onClick={handleProceedToPayment}
                                disabled={!selectedPlanDetails || isLoading || isUpgrading || isPlanChanged}
                                className="w-full md:w-auto px-8 flex items-center justify-center gap-2 h-10 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-sm transition-colors shadow-md shadow-blue-600/20"
                            >
                                <CreditCard size={16} />
                                Proceed to Payment
                                <ArrowRight size={14} className="ml-1" />
                            </button>
                        )}
                    </>
                )}
            </div>

        </div>
    );
};

export default PlanSelection;